<script setup lang="ts">
import { ref, onBeforeMount, watch } from "vue";
import { FormLabel, FormInput } from "../../../base-components/Form";
import FormSelect from "../../../base-components/Form/FormSelect.vue";
import Button from "../../../base-components/Button";
import Preview from "../../../base-components/Preview";
import LoadingIcon from "../../../base-components/LoadingIcon";
import { useMesasStore } from "../../../services/mesas";
import { useSectoresStore } from "../../../services/sectores";
import { useMensajesStore } from "../../../services/mensajes";

const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const mensajesStore = useMensajesStore();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

onBeforeMount(() => {
  if (sectoresStore.sectores.length === 0) sectoresStore.getFromFirestore();
});

const cargando = ref(false);
const nombre = ref("");
const capacidad = ref(2);
/** Sector de la mesa (id real del sector). */
const sectorId = ref<string>("");

watch(
  () => sectoresStore.sectores,
  sectores => {
    if (!sectorId.value && sectores?.length) {
      sectorId.value =
        sectores.find(
          s => (s.nombre ?? "").trim().toLowerCase() === "general"
        )?.id ?? sectores[0].id;
    }
  },
  { immediate: true }
);

const guardar = async () => {
  const nombreTrim = nombre.value.trim();
  if (!nombreTrim) {
    mensajesStore.mostrarModal(
      "Nombre requerido",
      "Ingresá el nombre de la mesa.",
      mensajesStore.mensajeType.error
    );
    return;
  }
  if (capacidad.value < 1) {
    mensajesStore.mostrarModal(
      "Capacidad inválida",
      "La capacidad debe ser al menos 1.",
      mensajesStore.mensajeType.error
    );
    return;
  }

  cargando.value = true;
  try {
    const result = await mesasStore.crearMesa(
      nombreTrim,
      Number(capacidad.value),
      mesasStore.mesas.length,
      sectorId.value
    );
    if (result === true) {
      mensajesStore.mostrarToast(
        "Mesa creada",
        `Se creó la mesa "${nombreTrim}" correctamente.`,
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    } else if (typeof result === "string") {
      mensajesStore.mostrarModal(
        "Nombre duplicado",
        result,
        mensajesStore.mensajeType.error
      );
    } else {
      mensajesStore.mostrarModal(
        "Error",
        "No se pudo crear la mesa. Intentá de nuevo.",
        mensajesStore.mensajeType.error
      );
    }
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Preview class="mt-5 box">
    <div class="p-5">
      <h2 class="text-base font-medium mb-4">Crear mesa</h2>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <FormLabel htmlFor="mesa-nombre">Nombre</FormLabel>
          <FormInput
            id="mesa-nombre"
            v-model="nombre"
            type="text"
            placeholder="Ej: Mesa 1"
            class="mt-1"
          />
        </div>
        <div>
          <FormLabel htmlFor="mesa-capacidad">Capacidad (personas)</FormLabel>
          <FormInput
            id="mesa-capacidad"
            v-model.number="capacidad"
            type="number"
            min="1"
            class="mt-1"
          />
        </div>
        <div v-if="sectoresStore.sectores.length > 0">
          <FormLabel htmlFor="mesa-sector">Sector</FormLabel>
          <FormSelect
            id="mesa-sector"
            v-model="sectorId"
            class="mt-1"
          >
            <option
              v-for="s in sectoresStore.sectores"
              :key="s.id"
              :value="s.id"
            >
              {{ s.nombre }}
            </option>
          </FormSelect>
        </div>
      </div>
      <div class="flex gap-2 mt-4">
        <Button
          variant="primary"
          :disabled="cargando"
          @click="guardar"
        >
          <LoadingIcon v-if="cargando" icon="oval" class="w-4 h-4 mr-2" />
          Crear mesa
        </Button>
        <Button variant="outline-secondary" :disabled="cargando" @click="emit('cerrar')">
          Cancelar
        </Button>
      </div>
    </div>
  </Preview>
</template>
